import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { getAnalyticsRange, DailyProgramMetrics } from 'api/apiClient';

interface AnalyticsSectionProps {
  programId: string;
  onError: (message: string) => void;
  onLoad?: (loadFn: () => void) => void; // Callback to expose the load function
}

type ViewMode = 'daily' | 'aggregate';

const AnalyticsSection: React.FC<AnalyticsSectionProps> = ({ programId, onError, onLoad }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DailyProgramMetrics[] | null>(null);
  const [aggregateData, setAggregateData] = useState<any>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  
  // Query parameters
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(7, 'days'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs().subtract(1, 'day'));
  const [viewMode, setViewMode] = useState<ViewMode>('daily');

  const loadAnalytics = useCallback(async () => {
    if (!programId) {
      onError('Invalid program ID');
      return;
    }

    setLoading(true);
    try {
      const response = await getAnalyticsRange({
        start: startDate.format('YYYY-MM-DD'),
        end: endDate.format('YYYY-MM-DD'),
        programId,
        aggregate: viewMode === 'aggregate',
        sortBy: viewMode === 'aggregate' ? 'total_unique_viewers' : undefined,
        order: 'desc',
      });

      if (response.success) {
        if (viewMode === 'aggregate' && Array.isArray(response.data)) {
          setAggregateData(response.data[0] || null);
          setData(null);
        } else if (viewMode === 'daily' && typeof response.data === 'object' && !Array.isArray(response.data)) {
          // Convert date-keyed object to array
          const dailyData: DailyProgramMetrics[] = [];
          Object.entries(response.data).forEach(([date, metrics]) => {
            if (Array.isArray(metrics)) {
              dailyData.push(...metrics);
            }
          });
          setData(dailyData.sort((a, b) => a.day.localeCompare(b.day)));
          setAggregateData(null);
        } else {
          setData(null);
          setAggregateData(null);
        }
      }
      setHasLoaded(true);
    } catch (error: any) {
      console.error('Error loading analytics:', error);
      onError(error?.response?.data?.error || 'Failed to load analytics');
      setData(null);
      setAggregateData(null);
      setHasLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [programId, startDate, endDate, viewMode, onError]);

  // Expose the initial load function to the parent
  useEffect(() => {
    if (onLoad) {
      onLoad(() => {
        if (!hasLoaded) {
          loadAnalytics();
        }
      });
    }
  }, [onLoad, hasLoaded, loadAnalytics]);

  const handleRefresh = () => {
    loadAnalytics();
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(Math.round(num));
  };

  const formatPercent = (num: number): string => {
    return `${(num * 100).toFixed(1)}%`;
  };

  const renderDailyCharts = () => {
    if (!data || data.length === 0) {
      return <Alert severity="info">No data available for the selected date range.</Alert>;
    }

    return (
      <Stack spacing={3}>
        {/* Unique Viewers */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Unique Viewers</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="day" 
                tickFormatter={(date) => dayjs(date).format('MMM D')}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(date) => dayjs(date).format('MMM D, YYYY')}
                formatter={(value: number) => formatNumber(value)}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="unique_viewers" 
                stroke="#8884d8" 
                fill="#8884d8" 
                name="Unique Viewers"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Paper>

        {/* Listening Minutes */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Listening Minutes</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="day" 
                tickFormatter={(date) => dayjs(date).format('MMM D')}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(date) => dayjs(date).format('MMM D, YYYY')}
                formatter={(value: number) => formatNumber(value)}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="minutes_est" 
                stroke="#82ca9d" 
                fill="#82ca9d" 
                name="Minutes"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Paper>

        {/* Requests Comparison */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Manifest & Segment Requests</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="day" 
                tickFormatter={(date) => dayjs(date).format('MMM D')}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(date) => dayjs(date).format('MMM D, YYYY')}
                formatter={(value: number) => formatNumber(value)}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="manifest_requests" 
                stroke="#8884d8" 
                name="Manifest Requests"
              />
              <Line 
                type="monotone" 
                dataKey="segment_requests" 
                stroke="#82ca9d" 
                name="Segment Requests"
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>

        {/* Cache Hit Rates */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Cache Hit Rates</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="day" 
                tickFormatter={(date) => dayjs(date).format('MMM D')}
              />
              <YAxis 
                tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
              />
              <Tooltip 
                labelFormatter={(date) => dayjs(date).format('MMM D, YYYY')}
                formatter={(value: number) => formatPercent(value)}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="manifest_cache_hit_rate" 
                stroke="#ffc658" 
                name="Manifest Cache"
              />
              <Line 
                type="monotone" 
                dataKey="segment_cache_hit_rate" 
                stroke="#ff7c7c" 
                name="Segment Cache"
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      </Stack>
    );
  };

  const renderAggregateView = () => {
    if (!aggregateData) {
      return <Alert severity="info">No aggregate data available.</Alert>;
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Total Unique Viewers</Typography>
            <Typography variant="h4">{formatNumber(aggregateData.total_unique_viewers)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Total Minutes</Typography>
            <Typography variant="h4">{formatNumber(aggregateData.total_minutes)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Days Active</Typography>
            <Typography variant="h4">{aggregateData.days_active}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Total Manifest Requests</Typography>
            <Typography variant="h4">{formatNumber(aggregateData.total_manifest_requests)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Total Segment Requests</Typography>
            <Typography variant="h4">{formatNumber(aggregateData.total_segment_requests)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Avg Manifest Cache Hit Rate</Typography>
            <Typography variant="h4">{formatPercent(aggregateData.avg_manifest_cache_hit_rate)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Avg Segment Cache Hit Rate</Typography>
            <Typography variant="h4">{formatPercent(aggregateData.avg_segment_cache_hit_rate)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">First Day</Typography>
            <Typography variant="h6">{dayjs(aggregateData.first_day).format('MMM D, YYYY')}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Last Day</Typography>
            <Typography variant="h6">{dayjs(aggregateData.last_day).format('MMM D, YYYY')}</Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={2}>
        {/* Controls */}
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => newValue && setStartDate(newValue)}
                maxDate={endDate}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => newValue && setEndDate(newValue)}
                minDate={startDate}
                maxDate={dayjs()}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
              />
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode) => newMode && setViewMode(newMode)}
                size="small"
              >
                <ToggleButton value="daily">Daily</ToggleButton>
                <ToggleButton value="aggregate">Aggregate</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Viewing analytics for program: {programId}
            </Typography>
          </Stack>
        </Paper>

        {/* Charts or Aggregate View */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : viewMode === 'daily' ? (
          renderDailyCharts()
        ) : (
          renderAggregateView()
        )}
      </Stack>
    </LocalizationProvider>
  );
};

export default AnalyticsSection;
